import { gsap } from "gsap"

const DETECTOR_KEY = "__intrepidMissileTargetingDetector"
const INSTANCE_KEY = "__intrepidMissileTargetingInstance"
const ACTIVATION_CODE = "IDDQD"

const defaults = {
  rootSelector: "[data-missile-targeting]",
  stageSelector: "[data-missile-stage]",
  targetSelector: "[data-missile-target]",
  resetSelector: "[data-missile-reset]",
  launchOrigin: "bottom-left",
  lockDuration: 0.65,
  flightDuration: 0.9,
  impactDuration: 0.55,
  resetDelay: 0,
  particleCount: 32,
  destroyTarget: false,
  autoReset: false,
  reducedMotion: "auto",
  debug: false,
}

const autoTargetSelector = "#main section, #main article, #main .paragraph"
const autoExcludedSelector = [
  "form",
  "dialog",
  "[role='dialog']",
  "nav",
  "header",
  "footer",
  "[data-react]",
  ".admin-bar",
  ".toolbar",
  ".modal",
  ".takeover",
  ".search",
].join(",")
const interactiveSelector =
  "a[href], button, input, select, textarea, summary, [contenteditable='true'], [tabindex]"

function getAttributeState(element, name) {
  return {
    exists: element.hasAttribute(name),
    value: element.getAttribute(name),
  }
}

function restoreAttribute(element, name, attribute) {
  if (attribute.exists) {
    element.setAttribute(name, attribute.value === null ? "" : attribute.value)
    return
  }

  element.removeAttribute(name)
}

function isEditableTarget(element) {
  return Boolean(
    element &&
      element.closest(
        "input, textarea, select, [contenteditable=''], [contenteditable='true']",
      ),
  )
}

function isVisibleTarget(element, automaticallyDiscovered = false) {
  if (
    !element.isConnected ||
    element.closest("[data-missile-disabled], [hidden], [aria-hidden='true']")
  ) {
    return false
  }

  if (
    automaticallyDiscovered &&
    (element.closest(autoExcludedSelector) ||
      element.querySelector(
        "form, dialog, [role='dialog'], [data-react], .modal, .takeover",
      ))
  ) {
    return false
  }

  const style = window.getComputedStyle(element)
  const rect = element.getBoundingClientRect()

  return (
    style.display !== "none" &&
    style.visibility !== "hidden" &&
    Number(style.opacity) !== 0 &&
    rect.width >= 24 &&
    rect.height >= 24 &&
    element.getClientRects().length > 0
  )
}

function getElementCenter(element, stage, viewportStage) {
  const elementRect = element.getBoundingClientRect()
  const stageRect = viewportStage
    ? { left: 0, top: 0 }
    : stage.getBoundingClientRect()

  return {
    x: elementRect.left - stageRect.left + elementRect.width / 2,
    y: elementRect.top - stageRect.top + elementRect.height / 2,
    width: elementRect.width,
    height: elementRect.height,
  }
}

function getLaunchOrigin(stage, launchOrigin, viewportStage) {
  const rect = viewportStage
    ? { width: window.innerWidth, height: window.innerHeight }
    : stage.getBoundingClientRect()
  const inset = Math.min(72, Math.max(28, rect.width * 0.06))

  if (typeof launchOrigin === "object") {
    return {
      x: Number(launchOrigin.x) || 0,
      y: Number(launchOrigin.y) || 0,
    }
  }

  const origins = {
    "bottom-left": { x: inset, y: rect.height - inset },
    "bottom-right": { x: rect.width - inset, y: rect.height - inset },
    "top-left": { x: inset, y: inset },
    "top-right": { x: rect.width - inset, y: inset },
  }

  return origins[launchOrigin] || origins["bottom-left"]
}

function createFlightPoints(origin, destination, curve = 0.25) {
  const dx = destination.x - origin.x
  const dy = destination.y - origin.y
  const distance = Math.hypot(dx, dy)

  if (distance < 1) {
    return { origin, control: { ...origin }, destination }
  }

  const midpoint = {
    x: origin.x + dx * 0.5,
    y: origin.y + dy * 0.5,
  }

  return {
    origin,
    control: {
      x: midpoint.x - (dy / distance) * distance * curve,
      y: midpoint.y + (dx / distance) * distance * curve,
    },
    destination,
  }
}

function quadraticBezier(start, control, end, progress) {
  const inverse = 1 - progress

  return {
    x:
      inverse * inverse * start.x +
      2 * inverse * progress * control.x +
      progress * progress * end.x,
    y:
      inverse * inverse * start.y +
      2 * inverse * progress * control.y +
      progress * progress * end.y,
  }
}

function createElement(tagName, className, attributes = {}) {
  const element = document.createElement(tagName)
  element.className = className

  Object.entries(attributes).forEach(([name, value]) => {
    element.setAttribute(name, value)
  })

  return element
}

function createEffects(viewportStage) {
  const effects = createElement("div", "missile-targeting__effects")
  const hud = createElement("div", "missile-targeting__hud")
  const status = createElement("span", "missile-targeting__status")
  const reticle = createElement("div", "missile-targeting__reticle")
  const reticleRing = createElement("span", "missile-targeting__reticle-ring")
  const reticleCrosshair = createElement(
    "span",
    "missile-targeting__reticle-crosshair",
  )
  const reticleLabel = createElement("span", "missile-targeting__reticle-label")
  const projectile = createElement("div", "missile-targeting__projectile")
  const trail = createElement("span", "missile-targeting__trail")
  const flash = createElement("div", "missile-targeting__impact-flash")
  const shockwave = createElement("div", "missile-targeting__shockwave")
  const particles = createElement("div", "missile-targeting__particles")
  const live = createElement(
    "span",
    "missile-targeting__live visually-hidden",
    { "aria-live": "polite" },
  )

  status.textContent = "TARGETING SYSTEM // STANDBY"
  reticleLabel.textContent = "LOCKING"
  hud.append(status)
  reticle.append(reticleRing, reticleCrosshair, reticleLabel)
  projectile.append(trail)
  effects.append(hud, reticle, projectile, flash, shockwave, particles, live)
  hud.setAttribute("aria-hidden", "true")
  reticle.setAttribute("aria-hidden", "true")
  projectile.setAttribute("aria-hidden", "true")
  flash.setAttribute("aria-hidden", "true")
  shockwave.setAttribute("aria-hidden", "true")
  particles.setAttribute("aria-hidden", "true")

  const result = {
    effects,
    flash,
    hud,
    live,
    particles,
    projectile,
    reticle,
    reticleLabel,
    shockwave,
    status,
  }

  if (viewportStage) {
    const controls = createElement("div", "missile-targeting__controls")
    const reset = createElement("button", "missile-targeting__control", {
      type: "button",
      "data-missile-reset": "",
    })
    const close = createElement(
      "button",
      "missile-targeting__control missile-targeting__control--close",
      { type: "button", "data-missile-close": "" },
    )
    reset.textContent = "RESET TARGETS"
    close.textContent = "CLOSE SYSTEM"
    controls.append(reset, close)
    effects.append(controls)
    result.close = close
    result.reset = reset
  }

  return result
}

function createTargetRecord(target, addedAutomatically) {
  return {
    target,
    addedAutomatically,
    attributes: {
      ariaHidden: getAttributeState(target, "aria-hidden"),
      missileTarget: getAttributeState(target, "data-missile-target"),
      role: getAttributeState(target, "role"),
      tabIndex: getAttributeState(target, "tabindex"),
    },
    className: target.getAttribute("class"),
    style: getAttributeState(target, "style"),
  }
}

function resolveReducedMotion(option) {
  if (option === "auto") {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  return Boolean(option)
}

function initializeMissileTargeting(root, options, config = {}) {
  if (root[INSTANCE_KEY]) return root[INSTANCE_KEY]

  const viewportStage = Boolean(config.viewportStage)
  let stage = viewportStage ? null : root.querySelector(options.stageSelector)

  if (!viewportStage && !stage) return null

  const previousFocus = document.activeElement
  const rootTargetAttribute = getAttributeState(root, "data-missile-targeting")
  const rootClassAttribute = getAttributeState(root, "class")
  const targetElements = viewportStage
    ? Array.from(document.querySelectorAll(autoTargetSelector)).filter(
        (target) => isVisibleTarget(target, true),
      )
    : Array.from(root.querySelectorAll(options.targetSelector)).filter(
        (target) => isVisibleTarget(target),
      )

  if (!targetElements.length) return null

  const stageClassAttribute = viewportStage
    ? null
    : getAttributeState(stage, "class")

  if (viewportStage) {
    stage = createElement(
      "div",
      "missile-targeting__stage missile-targeting__stage--viewport",
      { "data-missile-stage": "" },
    )
    document.body.append(stage)
    root.setAttribute("data-missile-targeting", "")
    root.classList.add("missile-targeting", "missile-targeting--active")
  } else {
    root.classList.add("missile-targeting")
    stage.classList.add("missile-targeting__stage")
  }

  const elements = createEffects(viewportStage)
  stage.append(elements.effects)

  const records = targetElements.map((target) => {
    const addedAutomatically = !target.matches(options.targetSelector)
    const record = createTargetRecord(target, addedAutomatically)

    if (addedAutomatically) target.setAttribute("data-missile-target", "")

    if (
      !target.matches(interactiveSelector) &&
      !target.querySelector(interactiveSelector)
    ) {
      target.setAttribute("tabindex", "0")
      target.setAttribute("role", "button")
    }

    return record
  })
  const state = {
    activeTarget: null,
    autoResetCall: null,
    destroyed: false,
    generatedElements: Object.values(elements).filter(
      (element) => element instanceof Element,
    ),
    listeners: [],
    phase: "idle",
    timeline: null,
  }
  const reducedMotion = resolveReducedMotion(options.reducedMotion)

  function announce(message) {
    elements.status.textContent = message.toUpperCase()
    if (elements.live) elements.live.textContent = message
  }

  function getTargetFromEvent(event) {
    const target = event.target.closest(options.targetSelector)
    if (!target || !targetElements.includes(target)) return null
    if (!target.isConnected || target.matches(".is-destroyed")) return null
    return target
  }

  function removeParticles() {
    elements.particles.replaceChildren()
  }

  function resetEffects() {
    gsap.set(elements.reticle, { clearProps: "all", autoAlpha: 0 })
    gsap.set(elements.projectile, { clearProps: "all", autoAlpha: 0 })
    gsap.set([elements.flash, elements.shockwave], {
      clearProps: "all",
      autoAlpha: 0,
    })
    removeParticles()
  }

  function resetTargets() {
    records.forEach((record) => {
      const { target } = record
      if (!target.isConnected) return

      target.classList.remove("is-targeted", "is-locked", "is-destroyed")
      restoreAttribute(target, "style", record.style)
      restoreAttribute(target, "aria-hidden", record.attributes.ariaHidden)
    })
  }

  function reset() {
    if (state.timeline) state.timeline.kill()
    if (state.autoResetCall) state.autoResetCall.kill()

    gsap.killTweensOf(state.generatedElements)
    gsap.killTweensOf(targetElements)
    state.timeline = null
    state.autoResetCall = null
    state.activeTarget = null
    state.phase = "idle"
    resetTargets()
    resetEffects()
    announce("Targeting system ready")
  }

  function addParticles(center) {
    removeParticles()
    gsap.set(elements.particles, { x: center.x, y: center.y })

    return Array.from({ length: options.particleCount }, (_, index) => {
      const particle = createElement(
        "span",
        `missile-targeting__particle missile-targeting__particle--${index % 3}`,
      )
      const angle = Math.random() * Math.PI * 2
      const distance = 35 + Math.random() * 115

      elements.particles.append(particle)
      gsap.set(particle, {
        rotation: Math.random() * 180,
        scale: 0.4 + Math.random() * 1.2,
        x: 0,
        y: 0,
      })
      particle.missileDestination = {
        rotation: `${Math.random() > 0.5 ? "+=" : "-="}${
          120 + Math.random() * 360
        }`,
        x: Math.cos(angle) * distance,
        y: Math.sin(angle) * distance,
      }

      return particle
    })
  }

  function createReducedTimeline(target, center) {
    state.phase = "locking"
    announce("Target locked")
    gsap.set(elements.reticle, {
      autoAlpha: 1,
      height: Math.min(150, Math.max(64, center.height * 0.65)),
      width: Math.min(150, Math.max(64, center.width * 0.65)),
      x: center.x,
      y: center.y,
    })

    return gsap
      .timeline()
      .to(elements.reticle, { duration: 0.15, opacity: 0.75 })
      .to(target, { duration: 0.15, filter: "brightness(1.35)" }, "<")
      .to(target, {
        duration: 0.2,
        filter: "brightness(0.55) grayscale(0.8)",
        opacity: 0,
      })
  }

  function completeShot(target) {
    if (target.contains(document.activeElement) && elements.reset) {
      elements.reset.focus()
    }

    target.classList.remove("is-targeted", "is-locked")
    target.classList.add("is-destroyed")
    target.setAttribute("aria-hidden", "true")

    if (options.destroyTarget) target.remove()

    resetEffects()
    state.timeline = null
    state.activeTarget = null
    state.phase = "idle"
    announce("Target destroyed")

    if (options.autoReset) {
      state.autoResetCall = gsap.delayedCall(options.resetDelay, reset)
    }
  }

  function fire(target) {
    if (state.phase !== "idle" || target.matches(".is-destroyed")) return

    const initialCenter = getElementCenter(target, stage, viewportStage)
    state.activeTarget = target
    target.classList.add("is-locked")
    elements.reticleLabel.textContent = "LOCKING"

    if (reducedMotion) {
      state.timeline = createReducedTimeline(target, initialCenter)
      state.timeline.eventCallback("onComplete", () => completeShot(target))
      return
    }

    state.phase = "locking"
    announce("Acquiring target")
    gsap.set(elements.reticle, {
      autoAlpha: 1,
      height: Math.min(180, Math.max(76, initialCenter.height * 0.72)),
      rotation: -38,
      scale: 1.55,
      width: Math.min(180, Math.max(76, initialCenter.width * 0.72)),
      x: initialCenter.x,
      y: initialCenter.y,
    })

    let flightPoints = null
    let lastPosition = null
    const flightProgress = { value: 0 }
    const particles = addParticles(initialCenter)
    const particleDuration = Math.max(0.25, options.impactDuration)
    const timeline = gsap.timeline({
      onComplete: () => completeShot(target),
    })

    state.timeline = timeline
    timeline
      .to(elements.reticle, {
        duration: options.lockDuration,
        ease: "power3.out",
        rotation: 0,
        scale: 1,
      })
      .call(() => {
        const destination = getElementCenter(target, stage, viewportStage)
        const origin = getLaunchOrigin(
          stage,
          options.launchOrigin,
          viewportStage,
        )

        flightPoints = createFlightPoints(origin, destination)
        lastPosition = origin
        state.phase = "firing"
        elements.reticleLabel.textContent = "LOCKED"
        announce("Target locked. Fired")
        gsap.set(elements.projectile, {
          autoAlpha: 1,
          rotation:
            Math.atan2(
              flightPoints.control.y - origin.y,
              flightPoints.control.x - origin.x,
            ) *
            (180 / Math.PI),
          x: origin.x,
          y: origin.y,
        })
      })
      .to(flightProgress, {
        duration: options.flightDuration,
        ease: "power2.in",
        value: 1,
        onUpdate: () => {
          if (!flightPoints) return
          const position = quadraticBezier(
            flightPoints.origin,
            flightPoints.control,
            flightPoints.destination,
            flightProgress.value,
          )
          const rotation =
            Math.atan2(
              position.y - lastPosition.y,
              position.x - lastPosition.x,
            ) *
            (180 / Math.PI)

          gsap.set(elements.projectile, {
            rotation,
            x: position.x,
            y: position.y,
          })
          lastPosition = position
        },
      })
      .call(() => {
        const center = getElementCenter(target, stage, viewportStage)
        state.phase = "impact"
        gsap.set(elements.projectile, { autoAlpha: 0 })
        gsap.set([elements.flash, elements.shockwave, elements.particles], {
          x: center.x,
          y: center.y,
        })
      })
      .to(
        elements.flash,
        {
          autoAlpha: 1,
          duration: 0.08,
          scale: 1.3,
          yoyo: true,
          repeat: 1,
        },
        ">",
      )
      .fromTo(
        elements.shockwave,
        { autoAlpha: 0.9, scale: 0.2 },
        {
          autoAlpha: 0,
          duration: particleDuration,
          ease: "power2.out",
          scale: 2.4,
        },
        "<",
      )
      .to(
        target,
        {
          duration: Math.min(0.24, particleDuration * 0.45),
          filter: "brightness(1.8) saturate(1.4)",
          keyframes: [
            { rotation: -1.5, x: -7, y: 2 },
            { rotation: 1.2, x: 8, y: -3 },
            { rotation: -0.6, x: -4, y: 2 },
          ],
        },
        "<",
      )
      .to(
        particles,
        {
          autoAlpha: 0,
          duration: particleDuration,
          ease: "power2.out",
          rotation: (index, particle) => particle.missileDestination.rotation,
          scale: 0.1,
          stagger: { amount: 0.12, from: "random" },
          x: (index, particle) => particle.missileDestination.x,
          y: (index, particle) => particle.missileDestination.y,
        },
        "<",
      )
      .to(
        target,
        {
          duration: particleDuration,
          ease: "power2.in",
          filter: "brightness(0.45) grayscale(0.85)",
          opacity: 0,
          rotation: () => (Math.random() - 0.5) * 5,
          scale: 0.82,
        },
        "<0.08",
      )
  }

  function destroy() {
    reset()
    state.listeners.forEach(({ element, type, listener, listenerOptions }) =>
      element.removeEventListener(type, listener, listenerOptions),
    )
    records.forEach((record) => {
      const { target } = record
      if (!target.isConnected) return

      restoreAttribute(
        target,
        "data-missile-target",
        record.attributes.missileTarget,
      )
      restoreAttribute(target, "tabindex", record.attributes.tabIndex)
      restoreAttribute(target, "role", record.attributes.role)
      if (record.className === null) target.removeAttribute("class")
    })
    elements.effects.remove()

    if (viewportStage) stage.remove()

    restoreAttribute(root, "class", rootClassAttribute)
    restoreAttribute(root, "data-missile-targeting", rootTargetAttribute)
    if (stageClassAttribute) {
      restoreAttribute(stage, "class", stageClassAttribute)
    }

    delete root[INSTANCE_KEY]
    if (previousFocus && previousFocus.isConnected) previousFocus.focus()
  }

  function onPointerOver(event) {
    const target = getTargetFromEvent(event)
    if (target && state.phase === "idle") target.classList.add("is-targeted")
  }

  function onPointerOut(event) {
    const target = getTargetFromEvent(event)
    if (target && !target.contains(event.relatedTarget)) {
      target.classList.remove("is-targeted")
    }
  }

  function onFocusIn(event) {
    const target = getTargetFromEvent(event)
    if (target && state.phase === "idle") target.classList.add("is-targeted")
  }

  function onFocusOut(event) {
    const target = getTargetFromEvent(event)
    if (target && !target.contains(event.relatedTarget)) {
      target.classList.remove("is-targeted")
    }
  }

  function onClick(event) {
    if (
      event.target.closest(".missile-targeting__controls") ||
      event.target.closest(options.resetSelector)
    ) {
      return
    }
    const target = getTargetFromEvent(event)
    if (!target) return

    event.preventDefault()
    event.stopImmediatePropagation()
    fire(target)
  }

  function onKeyDown(event) {
    if (event.key === "Escape" && viewportStage) {
      event.preventDefault()
      destroy()
      return
    }

    if (event.key !== "Enter" && event.key !== " ") return
    const target = getTargetFromEvent(event)
    if (!target) return

    event.preventDefault()
    event.stopImmediatePropagation()
    fire(target)
  }

  function addListener(element, type, listener, listenerOptions) {
    element.addEventListener(type, listener, listenerOptions)
    state.listeners.push({ element, listener, listenerOptions, type })
  }

  addListener(root, "pointerover", onPointerOver, true)
  addListener(root, "pointerout", onPointerOut, true)
  addListener(root, "focusin", onFocusIn, true)
  addListener(root, "focusout", onFocusOut, true)
  addListener(root, "click", onClick, true)
  addListener(root, "keydown", onKeyDown, true)

  root.querySelectorAll(options.resetSelector).forEach((resetButton) => {
    addListener(resetButton, "click", reset)
  })
  if (elements.close) addListener(elements.close, "click", destroy)

  resetEffects()
  announce("Targeting system ready")

  const instance = { destroy, fire, reset, state }
  root[INSTANCE_KEY] = instance

  if (options.debug) {
    console.info("Missile targeting initialized", {
      root,
      targets: targetElements.length,
      viewportStage,
    })
  }

  return instance
}

function installActivationDetector(options) {
  if (document[DETECTOR_KEY]) return

  let current = 0
  const onKeyDown = (event) => {
    if (
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      isEditableTarget(event.target)
    ) {
      current = 0
      return
    }

    const key = event.key.toUpperCase()
    if (key === ACTIVATION_CODE[current]) {
      current += 1
    } else {
      current = key === ACTIVATION_CODE[0] ? 1 : 0
    }

    if (current !== ACTIVATION_CODE.length) return
    current = 0

    const hasAuthoredInstance = Array.from(
      document.querySelectorAll(options.rootSelector),
    ).some((root) => root !== document.body && root[INSTANCE_KEY])

    if (!document.body[INSTANCE_KEY] && !hasAuthoredInstance) {
      initializeMissileTargeting(
        document.body,
        { ...options, targetSelector: defaults.targetSelector },
        {
          viewportStage: true,
        },
      )
    }
  }

  document.addEventListener("keydown", onKeyDown, false)
  document[DETECTOR_KEY] = onKeyDown
}

/**
 * Initialize authored missile-targeting roots and the site-wide IDDQD trigger.
 *
 * Authored markup may provide data-missile-targeting, data-missile-stage,
 * data-missile-target, and an optional data-missile-reset button. When no
 * authored root is active, IDDQD creates a temporary viewport targeting mode.
 */
export function MissileTargeting(options = {}) {
  const settings = { ...defaults, ...options }
  const roots = document.querySelectorAll(settings.rootSelector)

  roots.forEach((root) => initializeMissileTargeting(root, settings))
  installActivationDetector(settings)
}
